/**
 * Signup popup timing and state.
 * - Opens after `data-delay` seconds or `data-scroll` % of the page, whichever comes first,
 *   only when the section is enabled (`data-enabled`).
 * - Never again after a signup; after closing, waits `data-frequency` days.
 * - After the form posts (page reloads), reopens to show the success state or the error.
 * - `?pj-popup=preview` opens it right away regardless of settings (testing on the live store).
 * Desktop opens as a modal; phones get a non-modal bottom panel that doesn't block the page.
 */
const STORE_KEY = 'pj-popup';
const DAY = 24 * 60 * 60 * 1000;

/** @returns {{ subscribed?: boolean, closedAt?: number }} */
function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

/** @param {{ subscribed?: boolean, closedAt?: number }} state */
function writeState(state) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ ...readState(), ...state }));
  } catch {
    /* storage blocked: the popup may show again next visit */
  }
}

class PjPopup extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;
  #opened = false;

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-pj-popup-dialog]'));
  }

  connectedCallback() {
    const { signal } = this.#abort;
    const dialog = this.dialog;
    if (!dialog) return;

    this.addEventListener('click', this.#onClick, { signal });
    dialog.addEventListener('close', () => this.#remember(), { signal });
    dialog.addEventListener(
      'click',
      (e) => {
        if (e.target === dialog) dialog.close(); // backdrop click
      },
      { signal }
    );
    dialog.querySelector('form')?.addEventListener('submit', () => writeState({ subscribed: true }), { signal });

    const preview = new URLSearchParams(location.search).get('pj-popup') === 'preview';
    const success = Boolean(dialog.querySelector('[data-pj-popup-success]'));
    const error = Boolean(dialog.querySelector('.pj-popup__error'));

    // Back from a form post: show the result.
    if (success || error) {
      if (success) writeState({ subscribed: true });
      if (error) writeState({ subscribed: false });
      this.#open();
      return;
    }

    if (preview || this.hasAttribute('data-design-mode')) {
      if (preview) this.#open();
      document.addEventListener(
        'shopify:section:select',
        (e) => {
          if (!this.contains(/** @type {Node} */ (e.target))) return;
          this.#opened = false;
          this.#open();
        },
        { signal }
      );
      document.addEventListener('shopify:section:deselect', (e) => this.contains(/** @type {Node} */ (e.target)) && dialog.close(), { signal });
      return;
    }

    if (!this.hasAttribute('data-enabled')) return;

    const state = readState();
    if (state.subscribed) return;
    const frequency = Number(this.dataset.frequency ?? 30) * DAY;
    if (state.closedAt && Date.now() - state.closedAt < frequency) return;

    const delay = Number(this.dataset.delay ?? 8) * 1000;
    const scroll = Number(this.dataset.scroll ?? 0);
    this.#timer = window.setTimeout(() => this.#open(), delay);

    if (scroll > 0) {
      // Horizon scrolls .page-wrapper on desktop and the document on mobile; capture both.
      const onScroll = () => {
        const wrapper = document.querySelector('.page-wrapper');
        const el = wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : document.documentElement;
        const max = el.scrollHeight - el.clientHeight;
        if (max > 0 && (el.scrollTop / max) * 100 >= scroll) this.#open();
      };
      document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    clearTimeout(this.#timer);
  }

  #open() {
    const dialog = this.dialog;
    if (!dialog || this.#opened || dialog.open) return;
    this.#opened = true;
    clearTimeout(this.#timer);
    // Don't stack on top of another open dialog (cart drawer, menu, search).
    if (document.querySelector('dialog[open]')) {
      this.#opened = false;
      this.#timer = window.setTimeout(() => this.#open(), 5000);
      return;
    }
    if (window.matchMedia('(max-width: 749px)').matches) {
      dialog.show();
    } else {
      dialog.showModal();
    }
  }

  #remember() {
    if (!readState().subscribed) writeState({ closedAt: Date.now() });
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-pj-popup-close]')) {
      this.dialog?.close();
      return;
    }

    const copy = /** @type {HTMLElement | null} */ (target.closest('[data-pj-popup-copy]'));
    if (copy) {
      try {
        await navigator.clipboard.writeText(copy.dataset.pjPopupCopy ?? '');
        copy.textContent = 'Copied';
      } catch {
        copy.textContent = 'Select & copy';
      }
    }
  };
}

if (!customElements.get('pj-popup')) customElements.define('pj-popup', PjPopup);
